import { FC, useEffect, useMemo, useState, useCallback } from 'react'
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Chip as MuiChip,
  Autocomplete,
  Chip,
} from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import { AppForm, AppFormProps } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import GenericTable from 'src/components/table/GenericTable'
import { IBatchNumberDetails } from 'src/generated-types'
import { useZodForm } from 'src/hooks/zod-form'
import { GlobalStyled } from 'src/styles/globalPaddingStyles'
import { z } from 'zod'
import { styled } from '@mui/material/styles'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { toast } from 'react-toastify'

const schema = z.object({
  commonBatchNumbers: z.array(z.string()),
  componentIds: z.array(z.number()),
  positionIds: z.array(z.number()),
  isOverride: z.boolean().default(false),
})

export interface UpdateBatchNumberFormProps extends AppFormProps {
  pop: any
  positions: IBatchNumberDetails[]
  componentId: number
  isLoading: boolean
  productDescription: string
}

export const UpdateBatchNumberForm: FC<UpdateBatchNumberFormProps> = ({
  pop,
  onSubmit,
  positions,
  componentId,
  isLoading,
  productDescription,
}) => {
  const { t } = useTranslation(['position', 'common'])
  const [selectedIds, setSelectedIds] = useState<number[]>([componentId])
  const [selectedPositions, setSelectedPositions] = useState<IBatchNumberDetails[]>([])
  const [commonBatchNumbers, setCommonBatchNumbers] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const form = useZodForm(schema)

  useEffect(() => {
    setSelectedIds([componentId])
  }, [componentId])

  useEffect(() => {
    const selectedPositions = positions.filter(item => selectedIds.includes(item.id))
    const batchNumbers = selectedPositions?.map(item => item.potencialCertificateBNs)
    const commonBatchNumbers =
      batchNumbers.length > 0
        ? batchNumbers.reduce((accumulator, currentArray) => {
          return accumulator?.filter(value => currentArray?.includes(value))
        })
        : []

    setCommonBatchNumbers(commonBatchNumbers ?? [])
    setSelectedPositions(selectedPositions)
  }, [form, positions, selectedIds])

  useEffect(() => {
    form.setValue('commonBatchNumbers', commonBatchNumbers)
  }, [commonBatchNumbers, form])

  useEffect(() => {
    form.setValue('componentIds', selectedPositions?.map(p => p.id) ?? [])
    form.setValue('positionIds', selectedPositions?.map(p => p.positionId) ?? [])
  }, [form, selectedPositions])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        const value = inputValue.trim()
        if (value && !commonBatchNumbers.includes(value)) {
          setCommonBatchNumbers(prev => [...prev, value])
        }
        setInputValue('')
      }
      if (event.key === 'Backspace' && !inputValue) {
        setCommonBatchNumbers(prev => prev.slice(0, -1))
      }
    },
    [inputValue, commonBatchNumbers],
  )


  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      { field: 'positionName', headerName: t('position', { ns: 'position' }), flex: 1 },
      { field: 'positionDrawingNumber', headerName: t('position-drawing-number', { ns: 'position' }), flex: 1 },
      {
        field: 'potencialCertificateBNs',
        headerName: t('batch-numbers', { ns: 'position' }),
        flex: 1,
        renderCell: params => <span>{params.value.join(', ')}</span>,
      },
    ],
    [t],
  )
  const cleanupBatchNumbers = (values: string[]): string[] => {
    return Array.from(new Set(values.map(v => v.trim()).filter(v => v !== '')))
  }

  const handleSubmit = useCallback(() => {
    const pendingValue = inputValue.trim()

    if (pendingValue && commonBatchNumbers.includes(pendingValue)) {
      toast.warn(`"${pendingValue}" ${t('already-exists', { ns: 'position' })}`)
    }
    if (pendingValue && !commonBatchNumbers.includes(pendingValue)) {
      const updatedBatchNumbers = [...commonBatchNumbers, pendingValue]
      setCommonBatchNumbers(updatedBatchNumbers)
      form.setValue('commonBatchNumbers', updatedBatchNumbers)
    }
    setInputValue('')
    form.handleSubmit(onSubmit)()
  }, [form, inputValue, commonBatchNumbers, onSubmit])

  return (
    <GlobalStyled>
      <AppForm form={form} onSubmit={onSubmit}>
        <GenericDialogLayout
          title={t('update-batch-numbers', { ns: 'position' })}
          pop={pop}
          removedCloseButton={false}
          actions={[
            <MainButton key="position-form-btn" loading={isLoading} onClick={handleSubmit}>
              {t('update', { ns: 'common' })}
            </MainButton>,
          ]}
        >
          <Box className="pb-8-core">
            <Typography variant="subtitle2" fontWeight={'500'}>
              {productDescription}
            </Typography>
          </Box>
          <Box mb={2}>
            <GenericTable
              id="positions"
              columns={STATIC_COLUMNS}
              data={positions}
              getRowHeight={() => 'auto'}
              checkboxSelection={true}
              selectionModel={selectedIds}
              onSelectionModelChange={ids => {
                setSelectedIds(ids as number[])
              }}
              getRowClassName={params => (params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd')}
              height={600}
              autoHeight={false}
            />
          </Box>
          <Box className="pt-12-core">
            <Autocomplete<string, true, true, true>
              multiple
              freeSolo
              disableClearable
              options={[]}
              value={commonBatchNumbers}
              filterSelectedOptions
              onChange={(_, newValue) => {
                setCommonBatchNumbers(cleanupBatchNumbers(newValue))
              }}
              inputValue={inputValue}
              onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue)
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('update-batch-numbers', { ns: 'position' })}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      const currentValue = inputValue.trim()

                      if (currentValue && commonBatchNumbers.includes(currentValue)) {
                        toast.warn(`"${currentValue}" ${t('already-exists', { ns: 'position' })}`)
                      }
                    }
                  }}
                />
              )}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.watch('isOverride')}
                onChange={e => form.setValue('isOverride', e.target.checked)}
              />
            }
            label={t('replace-all-batch-numbers', { ns: 'position' }) as string}
          />
        </GenericDialogLayout>
      </AppForm>
    </GlobalStyled>
  )
}

export const StyledChip = styled(MuiChip)(({ theme }) => ({
  margin: '0.2rem 0.4rem 0.2rem 0',
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.primaryMain)}`,
  '& .MuiChip-deleteIcon': {
    color: getThemeColor(theme, MUIThemeColors.primaryMain),
    '&:hover': {
      color: getThemeColor(theme, MUIThemeColors.primaryDark),
    },
  },
}))
